/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import { Waypoint } from 'react-waypoint';
import { Sticky } from 'react-sticky';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import useOptimizedScroll from '../../../hooks/useOptimizedScroll';
import SearchSort from './SearchSort';
import Filters from './Filters';
import BlankSlate from '../../blocks/BlankSlate';
import Button from '../../../atoms/buttons/Button';
import Container from '../../blocks/Container';
import Headline from '../../common/Headline';
import { commonIcons } from '../../../atoms/icons/materialIcons';
import CloseIcon from '../../../atoms/icons/CloseIcon';
import { getBackgroundTheme, getColor, getTextColor } from '../../../style';
import { extractLocationData, displayUserLocation, pluralize, renderAvatar } from '../../../common';
import CardList from '../../../modules/CardList';
import Card from '../../../atoms/Card';
import BackToTop from '../../blocks/BackToTopContainer';
import { isDarkMode, scrollToTop } from '../../../utils/helpers';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../constants/mixpanel';
import TrackLoadingToMixpanel from '../../common/TrackLoadingToMixPanel';
import Spinner from '../../../atoms/loader/Spinner/Spinner';
import LoadingBackdrop from '../../../atoms/loader/LoadingBackdrop';
import useFetchSearch from './queries/useFetchSearch';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import searchDefaults from '../../../config/Search';
import { mapSearchViewEventProperties } from '../../../utils/mixpanel/searchView';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { useDetectSearchCurrentLocation } from './hooks/useDetectSearchCurrentLocation';
import { useDisableBodyScroll } from '../../../hooks/useDisableBodyScroll';
import { FONT_FAMILIES } from 'theme/material/constants';
import breakpoints from 'theme/material/breakpoints';

const LoadingContainer = styled('div')({
  paddingTop: '22px',
});

const ListContainer = styled('div')({
  paddingRight: '10px',
  paddingLeft: '10px',
});

const FilterHeaderContainer = styled(Container)(() => ({
  background: getBackgroundTheme().surface,
  padding: '0 15px',
  width: 'auto',
  maxWidth: 1400,
}));

const SortMatchesContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const Matches = styled('div')(() => ({
  fontSize: '18px',
  lineHeight: '23px',
  letterSpacing: -0.5,
  fontWeight: 600,
  ...getTextColor().DARK_2,
  [breakpoints.down(768.05)]: {
    fontSize: '16px',
  },
}));

const WaypointContainer = styled('div')`
  font-size: 16px;
  text-align: center;
  padding: 0 24px 24px;
`;

const FilterButtonText = styled('span')({
  marginRight: '5px',
});

const FilterHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 0',
  justifyContent: 'space-between',
});

const SearchSummary = styled('div')({
  fontSize: '16px',
  flexGrow: '1px',
});

const LocationText = styled('h1')(() => ({
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: '24px',
  lineHeight: '18px',
  letterSpacing: -0.3,
  fontWeight: 400,
  fontFamily: FONT_FAMILIES.secondary,
  ...getTextColor().DARK_4,
  [breakpoints.down(768.05)]: {
    fontSize: '22px',
  },
}));

// eslint-disable-next-line no-empty-pattern
const FilterModal = styled('div')(({}) => ({
  position: 'fixed',
  top: '66px',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 996,
  backgroundColor: getBackgroundTheme().block,
  overflowY: 'auto',
}));

const renderSearchProfileResults = (
  profile,
  isMobile,
  handleSearchResultCardSelect,
  trackProfileView,
  setMixpanelDataByKeyHandler,
  updateInitialProfileToProfilePage
) => {
  try {
    const age = profile.age;
    const avatar = renderAvatar(profile.avatar.data.urls);
    const city = profile.city;
    const username = profile.account ? profile.account.data.username : '';
    const hashId = profile.hash_id || username;
    const photoCount = parseInt(profile.approved_public_photos_count, 10);
    const privatePhotoCount = profile.private_photos_count ? parseInt(profile.private_photos_count, 10) : 0;
    const online = profile.online;
    const userVerified = profile.id_verified;
    const isSelfieVerified = profile.selfie_verified;

    return (
      <Card
        id={`user-card-${hashId}`}
        onClick={() => {
          handleSearchResultCardSelect(hashId);
          trackProfileView({ Source: 'Search', User: hashId });
          setMixpanelDataByKeyHandler({
            key: EVENT_NAMES.OFFER_MADE,
            payload: { source: TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewSearch },
          });

          updateInitialProfileToProfilePage(profile);
        }}
        key={`user-card-${hashId}`}
        age={age}
        city={city}
        username={username}
        hashId={hashId}
        photoCount={photoCount}
        privatePhotoCount={privatePhotoCount}
        online={online}
        cardWidth={isMobile ? '48%' : '24%'}
        cardMargin={isMobile ? '1%' : '.5%'}
        imgUrl={avatar}
        isVerified={userVerified}
        screenIsMobile={isMobile}
        isSelfieVerified={isSelfieVerified}
      />
    );
  } catch (e) {
    console.warn(e);
    return null;
  }
};

const SearchWithRQ = (props: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useMediaBreakpoints();
  const { storeCurrentLocation, checkLocationChange } = useDetectSearchCurrentLocation();

  const {
    resetFilters,
    resetIndividualFilter,
    resetFiltersState,
    searchLimit,
    search,
    saveFilters,
    handleSearchResultCardSelect,
    userProfile,
    currentUserLocation,
    updateInitialProfileToProfilePage,
  } = props;

  const [formData, setFormData] = useState({
    ...searchDefaults.filters,
    latitude: null,
    longitude: null,
    ...search.filters,
  });

  const [filterToggled, setFilterToggled] = useState(false);
  const { mixpanelData, trackProfileView, setMixpanelDataByKeyHandler } = useMixPanel();

  useDisableBodyScroll(filterToggled);

  const { data, isFetching, fetchNextPage, hasNextPage, isError, refetch, isFetchingNextPage } = useFetchSearch({
    ...formData,
  });

  const queryClient = useQueryClient();

  const searchProfiles = useMemo(() => {
    return data?.pages?.flatMap(page => {
      return [...(page?.data || [])];
    });
  }, [data?.pages]);

  const featuredProfiles = useMemo(() => {
    return data?.pages?.flatMap(page => {
      return [...(page?.meta?.featured_profiles?.data || [])];
    });
  }, [data?.pages]);

  const searchProfilesTotalCount = useMemo(() => {
    return data?.pages?.[0]?.meta?.pagination?.total ?? searchProfiles?.length ?? 0;
  }, [data?.pages, searchProfiles]);

  const { gender, account_type, looking_male, looking_female } =
    (userProfile.profile && userProfile.profile.data) || {};

  // hide and show the filter bar.
  const filterClickHandler = () => {
    scrollToTop('auto');
    setFilterToggled(!filterToggled);
  };

  const runNewSearch = (newData = false) => {
    const updatedData = newData ? newData : formData;

    saveFilters(updatedData);
    return;
  };

  useEffect(() => {
    if (!isEqual(formData, search.filters)) {
      setFormData(search.filters);
    }
  }, [formData, search.filters]);

  useEffect(() => {
    if (checkLocationChange(currentUserLocation)) {
      (async () => {
        queryClient.setQueryData(['search', { ...formData }], () => ({
          pages: [],
          pageParams: [],
        }));

        await refetch();
      })();
    }
  }, [currentUserLocation, storeCurrentLocation]);

  const profileResultList = () => {
    // Return null if the results are being fetched to show only the indicator.
    if (isFetching && isEmpty(searchProfiles)) return null;

    if (!isEmpty(searchProfiles)) {
      const ResultList = !isEmpty(searchProfiles)
        ? Object.values(searchProfiles).map(profile => {
            return renderSearchProfileResults(
              profile,
              isMobile,
              handleSearchResultCardSelect,
              trackProfileView,
              setMixpanelDataByKeyHandler,
              updateInitialProfileToProfilePage
            );
          })
        : [];

      // boosted Profiles
      const BoostedProfiles = !isEmpty(featuredProfiles)
        ? Object.values(featuredProfiles).map(profile => {
            return renderSearchProfileResults(
              profile,
              isMobile,
              handleSearchResultCardSelect,
              trackProfileView,
              setMixpanelDataByKeyHandler,
              updateInitialProfileToProfilePage
            );
          })
        : [];

      return <CardList isMobile={isMobile}>{[...BoostedProfiles, ...ResultList]}</CardList>;
    } else {
      return (
        <BlankSlate
          img="search"
          title="No members found"
          message="We couldn’t find anyone matching your search criteria. Try increasing the range and changing your filters."
        />
      );
    }
  };

  const saveLocation = place => {
    // apply location to state
    const loc = extractLocationData(place);

    setFormData(prev => {
      return {
        ...prev,
        ...loc,
      };
    });
  };

  const applyFiltersHandler = newData => {
    // apply filters to search

    setFormData(prev => {
      navigate(location.pathname, {
        replace: true,
        state: {
          saveSearch: true,
        },
      });
      // apply to search
      runNewSearch(newData);
      // hide filter menu.
      setFilterToggled(false);

      return {
        ...prev,
        ...newData,
      };
    });
  };

  const sortByOnChange = e => {
    const target = e.target;
    const newFormData = Object.assign({}, { ...formData }, { [target.id]: target.value });

    //  Apply the new Sort to the filters
    applyFiltersHandler(newFormData);
  };

  const filterButton = () => {
    const btnText = filterToggled ? null : 'Filter';
    const icon = filterToggled ? (
      <CloseIcon customStyle={{ stroke: getColor().BRAND_2, strokeWidth: 2, width: 19, height: 19 }} />
    ) : (
      <commonIcons.tune.default />
    );

    return (
      <Button
        buttonType="chromeless"
        icon={icon}
        iconPosition="right"
        onClick={filterClickHandler}
        css={{ paddingLeft: 8, paddingRight: 8, fontSize: 18, color: getColor().BRAND_2 }}
        customStyle={{
          paddingLeft: 8,
          paddingRight: 8,
          fontSize: 18,
          fontWeight: 600,
          fontFamily: FONT_FAMILIES.primary,
          lineHeight: '18px',
          letterSpacing: -0.3,
          color: getColor().BRAND_2,
          boxShadow: 'none',
          background: 'none',
        }}
      >
        <FilterButtonText>{btnText}</FilterButtonText>
      </Button>
    );
  };

  const clickToTop = e => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const renderSearchSummary = (display = true) => {
    const { city, country, region } = (userProfile.profile && userProfile.profile.data) || {};
    const userLoc = { city, country, region };
    const searchLoc = search.filters.city
      ? { city: search.filters.city, country: search.filters.country, region: search.filters.region }
      : userLoc;
    const searchLineLocation = displayUserLocation(userLoc, searchLoc, true);

    if (display) {
      return (
        <div>
          {(userLoc.city || searchLoc.city) && (
            <span>
              <LocationText
                onClick={() => {
                  filterClickHandler();
                }}
              >
                {searchLineLocation}
              </LocationText>
            </span>
          )}
        </div>
      );
    }
  };

  const matchesCount = () => {
    const featuredCount = featuredProfiles ? featuredProfiles.length : 0;

    return searchProfilesTotalCount + featuredCount;
  };

  const displayFilterNav = () => {
    return (
      <FilterHeader data-test-id="search-filter-wrap">
        <SearchSummary>
          {!filterToggled ? (
            renderSearchSummary()
          ) : (
            <Headline
              size="SM"
              breakPointSize="XS"
              style={{ fontWeight: 600, lineHeight: '18px', letterSpacing: -0.3 }}
            >
              Filters
            </Headline>
          )}
        </SearchSummary>
        <div data-test-id="search-filter-button">{filterButton()}</div>
      </FilterHeader>
    );
  };

  // Create a debounced version of fetchNextPage to avoid multiple calls when scrolling rapidly
  // This will be used by both the Waypoint component and the optimized scroll hook
  const debouncedFetchNextPage = useCallback(
    _debounce(() => {
      if (hasNextPage && !isFetching && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, 300), // 300ms debounce time, similar to AutocompleteMain's approach
    [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]
  );

  // Use optimized scroll to detect when user is approaching the bottom of the page
  // This gives us earlier detection than the Waypoint component
  useOptimizedScroll({
    initialThreshold: 800, // Start loading when user is 800px from the bottom
    debounceDelay: 300, // Same debounce delay as used in AutocompleteMain
    onThresholdReached: debouncedFetchNextPage, // Use the same debounced function
  });

  const renderWaypoint = () => {
    if (!isFetching && !isFetchingNextPage) {
      if (!isError) {
        return (
          <Waypoint
            scrollableAncestor={window}
            onEnter={debouncedFetchNextPage} // Use the same debounced function
            // Increase the bottom offset to start loading earlier
            // This gives a smoother experience as data loads before user reaches the bottom
            bottomOffset="-600px"
          />
        );
      } else {
        return (
          <Button fit buttonType="primaryRed" onClick={fetchNextPage} customStyle={{ textAlign: 'center' }}>
            Load More
          </Button>
        );
      }
    } else {
      return null;
    }
  };

  const renderMoreExists = () => {
    const renderSearchLimit = () => {
      return searchLimit ? (
        <div>
          In order to show you the most relevant results as quickly as possible, we have omitted the least relevant
          results past the 1,000 already displayed. Please refine your search filters to see different results.
        </div>
      ) : (
        <div>No More Results</div>
      );
    };

    const renderFetching = () => {
      return !isFetching ? <WaypointContainer>{renderSearchLimit()}</WaypointContainer> : null;
    };

    return hasNextPage ? <WaypointContainer>{renderWaypoint()}</WaypointContainer> : renderFetching();
  };

  const renderFilterHeaderContainer = style => {
    return (
      <FilterHeaderContainer
        style={style}
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        distanceFromTop={60}
      >
        {displayFilterNav()}
      </FilterHeaderContainer>
    );
  };

  useEffect(() => {
    if (data?.pages && data?.pages.length > 0) {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.SEARCH_VIEW,
        payload: {
          eventProperties: mapSearchViewEventProperties(
            formData,
            mixpanelData[EVENT_NAMES.SEARCH_VIEW],
            matchesCount()
          ),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pages]);

  return (
    <div>
      {filterToggled ? (
        <React.Fragment>
          <FilterModal style={{ top: isMobile ? 50 : 66 }}>
            <Sticky topOffset={60}>
              {({ style }) => renderFilterHeaderContainer({ ...style, width: 'calc(100% - 30px)' })}
            </Sticky>
            <Filters
              searchFilters={search.filters}
              filterClickHandler={filterClickHandler}
              applyFiltersHandler={applyFiltersHandler}
              resetFilters={resetFilters}
              resetIndividualFilter={resetIndividualFilter}
              resetFiltersState={resetFiltersState}
              saveLocation={saveLocation}
              accountType={account_type}
              lookingMale={looking_male}
              lookingFemale={looking_female}
              gender={gender}
              darkmode={isDarkMode()}
            />
          </FilterModal>
        </React.Fragment>
      ) : (
        <Container
          fullWidth
          css={{
            padding: '15px 0',
            maxWidth: '1400px !important',
            margin: '0 auto',
          }}
        >
          <Sticky topOffset={60}>
            {({ style }) => renderFilterHeaderContainer({ ...style, width: 'calc(100% - 30px)' })}
          </Sticky>
          <Container
            css={{
              marginBottom: 12,
              maxWidth: '1400px !important',
              padding: '0 30px 5px 15px !important',
            }}
          >
            {!isFetching && (
              <SortMatchesContainer>
                <Matches>
                  {Number(matchesCount()).toLocaleString()} {pluralize(matchesCount(), 'Match', 'Matches')}
                </Matches>
                <SearchSort
                  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                  order={formData && formData.order}
                  onChangeHandler={sortByOnChange}
                />
              </SortMatchesContainer>
            )}
          </Container>
          <ListContainer>{profileResultList()}</ListContainer>

          <LoadingContainer>
            {isFetching && <TrackLoadingToMixpanel page="Search" />}
            {/* Only show full backdrop when doing initial load, not when loading more */}
            <LoadingBackdrop active={isFetching && !isFetchingNextPage} showLoadingBg={false} />

            {/* Single loading indicator for next page loads */}
            {isFetchingNextPage && (
              <div style={{ textAlign: 'center', padding: '10px' }}>
                <Spinner customStyle={{ transform: 'scale(0.8)' }} />
              </div>
            )}

            {/* Show Waypoint for infinite scroll when more results exist */}
            {hasNextPage && !isFetchingNextPage && renderMoreExists()}

            <BackToTop isVisible={!isFetching} onClick={clickToTop} />
          </LoadingContainer>
        </Container>
      )}
    </div>
  );
};

export default SearchWithRQ;
